/**
 * sessionStorage composable - Full sessionStorage API wrapper
 *
 * This composable exposes the complete sessionStorage API and can be used
 * anywhere in the application, not just with the persist plugin.
 *
 * For persist plugin integration, use SessionStorageAdapter.
 */
export function useSessionStorage () {
	/**
     * Get an item from sessionStorage
     * @param {string} key - The key to retrieve
     * @returns {Promise<string|null>} The value or null if not found
     */
	async function getItem (key) {
		return sessionStorage.getItem(key);
	}

	/**
     * Set an item in sessionStorage
     * @param {string} key - The key to set
     * @param {string} value - The value to store
     * @returns {Promise<void>}
     */
	async function setItem (key, value) {
		sessionStorage.setItem(key, value);
	}

	/**
     * Remove an item from sessionStorage
     * @param {string} key - The key to remove
     * @returns {Promise<void>}
     */
	async function removeItem (key) {
		sessionStorage.removeItem(key);
	}

	/**
     * Clear all items from sessionStorage
     * @returns {Promise<void>}
     */
	async function clear () {
		sessionStorage.clear();
	}

	/**
     * Get the key at a specific index
     * @param {number} index - The index
     * @returns {Promise<string|null>} The key at the index or null
     */
	async function key (index) {
		return sessionStorage.key(index);
	}

	/**
     * Get the number of items in sessionStorage
     * @returns {number} Number of stored items
     */
	function getLength () {
		return sessionStorage.length;
	}

	/**
     * Get all keys in sessionStorage
     * @returns {Promise<string[]>} Array of all keys
     */
	async function keys () {
		const allKeys = [];
		for (let i = 0; i < sessionStorage.length; i++) {
			const k = sessionStorage.key(i);
			if (k) allKeys.push(k);
		}
		return allKeys;
	}

	/**
     * Check if a key exists
     * @param {string} key - The key to check
     * @returns {Promise<boolean>} True if exists
     */
	async function has (key) {
		return sessionStorage.getItem(key) !== null;
	}

	return {
		// Core sessionStorage API
		getItem,
		setItem,
		removeItem,
		clear,
		key,
		getLength,

		// Convenience methods
		keys,
		has,
	};
}
